import { useState } from "react";
import FileUpload from "../components/FileUpload";
import { trpc } from "../../main/components/ProviderContainer.tsx";

const hostDirty = import.meta.env.VITE_DEV_HOST || window.location.host;
const mode = import.meta.env.MODE;
const prefix = mode === "development" ? "http://" : "https://";
const host = hostDirty.replace("http://", "");
const url = `${prefix}${host}/comptaRest/upload`;

type UploadState = {
  uploadState: "stopped" | "started" | "error" | "success";
  currentFiles: string[];
  progress: { [key: string]: number };
  completedFiles: string[];
  filesWithError: string[];
  order: { [key: string]: number };
  counter: number;
};

type PreRapprochementSummary = {
  dryRun: boolean;
  attemptedGroups: number;
  matchedGroups: number;
  linkedLogLines: number;
  skippedGroups: { groupKey: string; reason: string }[];
  error?: string;
};

type SecuriteSocialeImportResult = {
  detectedEncoding: string;
  sourceFileHash: string;
  importId: string | null;
  insertedCount: number;
  skippedBecauseDuplicate: boolean;
  totalAmountCent: number;
  dryRun: boolean;
  preRapprochement: PreRapprochementSummary;
};

type UploadFileResult = {
  fileName: string;
  mimetype: string;
  kind: "securiteSocialeCsv" | "pdf" | "excel" | "unknown";
  success: boolean;
  message?: string;
  securiteSocialeImport?: SecuriteSocialeImportResult;
};

type UploadApiResponse = {
  success: boolean;
  fileResults: UploadFileResult[];
  globalError?: string;
};

type FastifyErrorResponse = {
  statusCode?: number;
  message?: string;
  error?: string;
};

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) {
    return error.message;
  }
  return "Erreur inconnue pendant l'upload";
};

const extractErrorMessageFromPayload = (payload: unknown) => {
  if (typeof payload !== "object" || payload === null) {
    return null;
  }

  const maybeUpload = payload as UploadApiResponse;
  if (typeof maybeUpload.globalError === "string" && maybeUpload.globalError) {
    return maybeUpload.globalError;
  }

  const maybeFastify = payload as FastifyErrorResponse;
  if (typeof maybeFastify.message === "string" && maybeFastify.message) {
    return maybeFastify.message;
  }

  if (typeof maybeFastify.error === "string" && maybeFastify.error) {
    return maybeFastify.error;
  }

  return null;
};

const isUploadApiResponse = (
  payload: unknown,
): payload is UploadApiResponse => {
  if (typeof payload !== "object" || payload === null) {
    return false;
  }

  const maybeResponse = payload as {
    success?: unknown;
    fileResults?: unknown;
  };

  return (
    typeof maybeResponse.success === "boolean" &&
    Array.isArray(maybeResponse.fileResults)
  );
};

const useUploadState = () => {
  const [uploadState, setUploadState] = useState<UploadState>({
    uploadState: "stopped",
    currentFiles: [],
    progress: {},
    completedFiles: [],
    filesWithError: [],
    order: {},
    counter: 0,
  });
  const [lastUploadResponse, setLastUploadResponse] =
    useState<UploadApiResponse | null>(null);

  const addFile = (file: File) => {
    setUploadState((uploadState: UploadState) => ({
      ...uploadState,
      uploadState: "started",
      currentFiles: [...uploadState.currentFiles, file.name],
      progress: { ...uploadState.progress, [file.name]: 0 },
      completedFiles: uploadState.completedFiles.filter((f) => f !== file.name),
      filesWithError: uploadState.filesWithError.filter((f) => f !== file.name),
      counter: uploadState.counter + 1,
      order: { ...uploadState.order, [file.name]: uploadState.counter },
    }));
  };

  const setProgress = (file: File, progress: number) => {
    setUploadState((uploadState: UploadState) => ({
      ...uploadState,
      progress: { ...uploadState.progress, [file.name]: progress },
    }));
  };

  const setUploaded = (file: File) => {
    setUploadState((uploadState: UploadState) => ({
      ...uploadState,
      currentFiles: uploadState.currentFiles.filter((f) => f !== file.name),
      completedFiles: [...new Set([...uploadState.completedFiles, file.name])],
      filesWithError: uploadState.filesWithError.filter((f) => f !== file.name),
    }));
  };

  const setErrored = (file: File) => {
    setUploadState((uploadState: UploadState) => ({
      ...uploadState,
      currentFiles: uploadState.currentFiles.filter((f) => f !== file.name),
      filesWithError: [...new Set([...uploadState.filesWithError, file.name])],
      completedFiles: uploadState.completedFiles.filter((f) => f !== file.name),
    }));
  };

  const utils = trpc.useContext();
  // const startUpload = (file: File) => {
  //   Relevés.insert(
  //     {
  //       file,
  //       chunkSize: "dynamic",
  //       onStart: () => addFile(file),
  //       onError: () => setErrored(file),
  //       onProgress: (progress: number) => setProgress(file, progress),
  //       onUploaded: () => {
  //         setUploaded(file);
  //         setTimeout(() => utils.banque.invalidate(), 2000); //hack, on met 2s pour que le serveur ait le temp de se mettre à jour
  //       }
  //     },
  //     true
  //   );
  // };

  const startMultipleUploads = (
    files: File[],
    options?: { dryRun?: boolean },
  ) => {
    if (files.length === 0) {
      return;
    }

    const dryRun = options?.dryRun ?? true;
    const uploadUrl = new URL(url);
    uploadUrl.searchParams.set("dryRun", dryRun ? "1" : "0");

    setLastUploadResponse(null);
    const data = new FormData();
    files.forEach((file, i) => {
      addFile(file);
      setProgress(file, 0);
      data.append(`file-${i}`, file, file.name);
    });

    // 👇 Uploading the files using the fetch API to the server
    fetch(uploadUrl.toString(), {
      method: "POST",
      body: data,
      credentials: "include",
    })
      .then(async (res) => {
        const rawBody = await res.text();
        let parsedBody: unknown = null;
        try {
          parsedBody = rawBody ? JSON.parse(rawBody) : null;
        } catch (_error) {
          parsedBody = null;
        }

        if (!res.ok) {
          const messageFromServer =
            extractErrorMessageFromPayload(parsedBody) ||
            rawBody ||
            `Erreur HTTP ${res.status}`;

          const syntheticResponse: UploadApiResponse = {
            success: false,
            globalError: messageFromServer,
            fileResults: files.map((file) => ({
              fileName: file.name,
              mimetype: file.type || "unknown",
              kind: "unknown",
              success: false,
              message: messageFromServer,
            })),
          };

          setLastUploadResponse(syntheticResponse);
          files.forEach(setErrored);
          setUploadState((uploadState) => ({
            ...uploadState,
            uploadState: "error",
          }));
          return;
        }

        if (parsedBody === true) {
          files.forEach(setUploaded);
          setUploadState((uploadState) => ({
            ...uploadState,
            uploadState: "success",
          }));
          utils.banque.invalidate();
          return;
        }

        if (!isUploadApiResponse(parsedBody)) {
          const message =
            extractErrorMessageFromPayload(parsedBody) ||
            "Réponse serveur invalide pendant l'upload";
          const syntheticResponse: UploadApiResponse = {
            success: false,
            globalError: message,
            fileResults: files.map((file) => ({
              fileName: file.name,
              mimetype: file.type || "unknown",
              kind: "unknown",
              success: false,
              message,
            })),
          };

          setLastUploadResponse(syntheticResponse);
          files.forEach(setErrored);
          setUploadState((uploadState) => ({
            ...uploadState,
            uploadState: "error",
          }));
          return;
        }

        const response = parsedBody;
        console.log(response);
        setLastUploadResponse(response);

        const successByName = new Map(
          response.fileResults.map((result) => [
            result.fileName,
            result.success,
          ]),
        );

        files.forEach((file) => {
          const success = successByName.get(file.name);
          if (success === true) {
            setUploaded(file);
          } else {
            setErrored(file);
          }
        });

        setUploadState((uploadState) => ({
          ...uploadState,
          uploadState: response.success ? "success" : "error",
        }));
        utils.banque.invalidate(); //todo mettre en place un vrai suivi de l'upload et du traitement des fichiers
      })
      .catch((err) => {
        const message = getErrorMessage(err);
        const syntheticResponse: UploadApiResponse = {
          success: false,
          globalError: message,
          fileResults: files.map((file) => ({
            fileName: file.name,
            mimetype: file.type || "unknown",
            kind: "unknown",
            success: false,
            message,
          })),
        };

        console.error(err);
        setLastUploadResponse(syntheticResponse);
        files.forEach(setErrored);
        setUploadState((uploadState) => ({
          ...uploadState,
          uploadState: "error",
        }));
      });
  };
  return {
    uploadState,
    startMultipleUploads,
    lastUploadResponse,
  };
};

const UploadContainer = () => {
  const [dryRun, setDryRun] = useState(false);
  const { startMultipleUploads, uploadState, lastUploadResponse } =
    useUploadState();
  const currentYear = new Date().getFullYear();
  return (
    <div>
      <FileUpload
        uploadState={uploadState}
        startUpload={startMultipleUploads}
        dryRun={dryRun}
        onDryRunChange={setDryRun}
        currentYear={currentYear}
        lastUploadResponse={lastUploadResponse}
      />
    </div>
  );
};

export default UploadContainer;
